import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, FileText, ClipboardList, ShieldCheck, Settings, LogOut, Upload, Wind, Wrench, Package2, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { DPassLogo } from './DPassLogo';

export function MobileSidebar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const navigation = [
    { name: t('nav.dashboard'),     href: '/',              icon: LayoutDashboard },
    { name: t('nav.passports'),     href: '/passports',     icon: FileText },
    { name: t('nav.importCsv'),     href: '/import',        icon: Upload },
    { name: t('nav.auditLog'),      href: '/audit-log',     icon: ClipboardList },
    { name: t('nav.compliance'),    href: '/compliance',    icon: ShieldCheck },
    { name: t('nav.cbamCarbon'),    href: '/cbam',          icon: Wind },
    { name: t('nav.repairability'), href: '/repairability', icon: Wrench },
    { name: t('nav.packaging'),     href: '/packaging',     icon: Package2 },
    { name: t('nav.settings'),      href: '/settings',      icon: Settings },
    { name: t('nav.docs'),          href: '/docs',          icon: BookOpen },
  ];

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="lg:hidden p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
        aria-label={t('layout.openNav')}
      >
        <Menu className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setOpen(false)} />

          <div className="absolute left-0 top-0 bottom-0 w-64 sidebar-gradient flex flex-col shadow-2xl animate-slide-up">
            <div className="h-16 px-5 flex items-center justify-between border-b border-white/5">
              <div className="flex items-center gap-2.5">
                <DPassLogo className="w-8 h-8" />
                <div>
                  <p className="text-white font-bold text-sm leading-none">D-Pass</p>
                  <p className="text-slate-500 text-xs mt-0.5 leading-none">{t('layout.subtitle')}</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label={t('layout.closeNav')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
              {navigation.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      active ? 'bg-brand-600/20 text-brand-400' : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-brand-400' : 'text-slate-500'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            <div className="px-3 pb-2 border-t border-white/5 pt-3">
              <button
                onClick={() => { logout(); navigate('/login'); setOpen(false); }}
                className="flex items-center gap-2.5 px-3 py-2 w-full rounded-xl text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-all text-sm"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.signOut')}
              </button>
            </div>

            <div className="px-4 pb-6 border-t border-white/5 pt-4">
              <div className="px-3 py-2.5 rounded-xl bg-brand-600/10 border border-brand-600/20">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
                  <span className="text-brand-400 text-xs font-semibold">{t('layout.euCompliant')}</span>
                </div>
                <p className="text-slate-500 text-xs">{t('layout.gdpr')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
